import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import { Avatar, Tooltip, Typography } from "@mui/material";
import { ConsumerInfoOut } from "@providers/hop-ord-server/api";
import dayjs from "dayjs";
import { Duration } from "dayjs/plugin/duration";

const formatDuration = (duration: Duration): string => {
  let formatString = "";
  if (duration.months() > 0) {
    formatString += "M [months], ";
  }
  if (duration.days() > 0) {
    formatString += "D [days], ";
  }
  if (duration.hours() > 0) {
    formatString += "H [hrs], ";
  }
  if (duration.minutes() > 0) {
    formatString += "m[m], ";
  }
  formatString += "s[s] ";
  return duration.format(formatString);
};

const IdleTimeIndicator = ({
  consumer,
  alert = false,
  pressure = false,
}: {
  consumer: ConsumerInfoOut;
  alert?: boolean;
  pressure?: boolean;
}) => {
  const detailMsg = alert
    ? "Check if the consumer is stuck."
    : "Probably healthy and waiting for a task.";

  const thresholds = [
    {
      minIdleTime: dayjs.duration(1, "week"),
      colour: "grey.400",
      Icon: CancelIcon,
      msg: "Idle for more than 1 week. Likely no longer exists.",
    },
    {
      minIdleTime: dayjs.duration(1, "day"),
      colour: pressure ? "error.main" : "warning.main",
      Icon: ErrorIcon,
      msg: `Idle for more than 1 day. ${pressure ? "Either stuck, or no longer exists." : ""}`,
    },
    {
      minIdleTime: dayjs.duration(1, "hour"),
      colour: alert ? "error.main" : "warning.main",
      Icon: ErrorIcon,
      msg: `Idle for more than 1 hour. ${detailMsg}`,
    },
    {
      minIdleTime: dayjs.duration(30, "minutes"),
      colour: alert ? "error.main" : "warning.main",
      Icon: alert ? ErrorIcon : HelpIcon,
      msg: `Idle for more than 30 minutes. ${detailMsg}`,
    },
    {
      minIdleTime: dayjs.duration(5, "minutes"),
      colour: alert ? "error.main" : "warning.main",
      Icon: HelpIcon,
      msg: `Idle for more than 5 minutes. ${detailMsg}`,
    },
  ];

  let Icon = CheckCircleIcon;
  let colour = "success.main";
  let msg = "Idle for less than 5 minutes.";
  const idleDuration = dayjs.duration(consumer.idleMillis, "milliseconds");
  for (const status of Object.values(thresholds)) {
    if (idleDuration.asMilliseconds() >= status.minIdleTime.asMilliseconds()) {
      Icon = status.Icon;
      colour = status.colour;
      msg = (alert ? "ALERT: " : "") + status.msg;
      break;
    }
  }

  return (
    <Tooltip
      arrow
      title={
        <>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>
            {msg}
          </Typography>
          <p>Name: {consumer.consumerName}</p>
          <p>Idle: {formatDuration(idleDuration)}</p>
        </>
      }
    >
      <Avatar
        sx={{ bgcolor: colour, height: 26, width: 26 }}
        data-testid={`consumer-idle-indicator-${consumer.consumerName}`}
      >
        <Icon fontSize="small" />
      </Avatar>
    </Tooltip>
  );
};

export default IdleTimeIndicator;
